import React, { useState, useEffect, useRef, useId } from 'react';
import { FocusTrap, createFocusTrap } from 'focus-trap';
import {
  disableBodyScroll,
  clearAllBodyScrollLocks,
  BodyScrollOptions
} from 'body-scroll-lock';
import { createDataLayerString } from '../../helpers/helpers';

type MobileFiltersProps = {
  children: React.ReactNode;
  label: string;
  containerRef: React.RefObject<HTMLDivElement>;
  refinementLayer?: boolean;
  customCloseLayer?: string;
  customOpenLayer?: string;
};

export const MOBILE_FILTERS_EVENTS = {
  close: 'mobile-filters:close'
};

function MobileFilters({
  children,
  label,
  containerRef,
  refinementLayer,
  customCloseLayer,
  customOpenLayer
}: MobileFiltersProps) {
  const [expanded, setExpanded] = useState(false);
  const [focusTrap, setFocusTrap] = useState<FocusTrap | null>(null);
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const accordionId = useId();

  const getDatalayer = () => {
    if (refinementLayer) {
      return expanded
        ? createDataLayerString([
            {
              event: 'mobile_filter_panel',
              event_category: 'refinement usage',
              event_action: 'close mobile filter'
            }
          ])
        : createDataLayerString([
            {
              event: 'mobile_filter_panel',
              event_category: 'refinement usage',
              event_action: 'open mobile filter'
            }
          ]);
    }

    if (customCloseLayer && expanded) {
      return customCloseLayer;
    }

    if (customOpenLayer && !expanded) {
      return customOpenLayer;
    }

    return undefined;
  };

  useEffect(() => {
    if (accordionRef.current) {
      const options = {
        reserveScrollBarGap: true,
        allowTouchMove: (el: HTMLElement) => {
          return el.closest('.mobile-filters__accordion') !== null;
        }
      } as BodyScrollOptions;

      const trap = createFocusTrap(accordionRef.current, {
        clickOutsideDeactivates: true,
        returnFocusOnDeactivate: true,
        onActivate() {
          disableBodyScroll(document.body, options);
        },
        onDeactivate() {
          clearAllBodyScrollLocks();
        }
      });

      setFocusTrap(trap);
    }

    window.addEventListener('scroll', handleContainerScrolled);
    window.addEventListener(MOBILE_FILTERS_EVENTS.close, handleClose);

    return () => {
      window.removeEventListener('scroll', handleContainerScrolled);
      window.removeEventListener(MOBILE_FILTERS_EVENTS.close, handleClose);
    };
  }, []);

  const handleClose = () => {
    setExpanded(false);
  };

  const handleAccordionClick = () => {
    setExpanded(!expanded);
    if (focusTrap) {
      if (expanded) {
        focusTrap.deactivate();

        let scrollableElement: HTMLElement | null = null;

        if (containerRef && containerRef.current) {
          scrollableElement = containerRef.current;
        } else if (accordionRef.current) {
          const parentSection = accordionRef.current.closest('section');
          scrollableElement = parentSection;
        }

        if (scrollableElement) {
          setTimeout(() => {
            scrollableElement?.scrollIntoView({ behavior: 'smooth' });
          }, 150);
        }
      } else {
        focusTrap.activate();
      }
    }
    setTimeout(() => {
      window.dispatchEvent(new Event('scroll'));
    }, 150);
  };

  const handleContainerScrolled = () => {
    if (containerRef.current && accordionRef.current) {
      const elementTop = containerRef.current.getBoundingClientRect().top;
      document.body.classList.toggle('mobile-filters', elementTop < 0);
      accordionRef.current.classList.toggle(
        'mobile-filters__accordion--fixed',
        elementTop < 0
      );
    }
  };

  return (
    <div
      className={`mobile-filters__accordion ${
        expanded ? 'mobile-filters__accordion--expanded' : ''
      }`}
      ref={accordionRef}
    >
      <button
        data-layer={getDatalayer()}
        className="accordion__item-trigger"
        aria-expanded={expanded}
        id={`trigger-${accordionId}`}
        aria-controls={`panel-${accordionId}`}
        onClick={handleAccordionClick}
      >
        <span className="mobile-filters__filters-title">{label}</span>
      </button>
      <div
        id={`panel-${accordionId}`}
        role="region"
        aria-labelledby={`trigger-${accordionId}`}
        className={`accordion__item-panel ${expanded ? '' : 'hidden'}`}
      >
        <div className="mobile-filters__filters-wrapper">{children}</div>
      </div>
    </div>
  );
}

export default MobileFilters;
